import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import AsyncState from "../../core/asyncState"
import {
    DialogBadge,
    DialogTopic,
    ISearchClientsByCriterionResponse,
    ISearchOperatorsByCriterionRequest,
    ISearchOperatorsByCriterionResponse,
    ReadonlyChat
} from "../../models/Dialogs/dialog"
import {
    ApiMessagesDU,
    AutoHoldingMessage,
    AutoHoldingTimer,
    DialogStartMessage,
    GetUpdatedMessagesRequest,
    MessageAttachment,
    MessageMeta,
    MessageInputViewData,
    UserIsTypingMessage
} from "../../models/Dialogs/message"
import { SystemError } from "../../core/error"
import { SurveyForm } from "../../models/Dialogs/surveyForm"
import { convertDialogTopicsToTree, getListOfSelectedIds } from "../../utility/dialogs/dialog-topics"
import { IAISuggestItemResponse } from "../../models/Dialogs/aiSuggestDTOs"
import { resetReducerState } from "../action"
import { SimpleChannel, SimpleChannelWithUserId, SimpleOperatorEmailChannel } from "../../models/channel"
import { ContentEditorCommands } from "../../components/ContentEditor/ContentEditor"
import { MessageSlots } from "../../models/slot"
import { SessionStatus, VideoCallState } from "../../components/Dialogs/Screensharing/types"
import { FastRepliesHotKeyWithId, HotKeyArticle } from "../../models/fastRepliesHotKeys"
import { CategoryTreeItem } from "../../models/category"
import { updateSidebarContext } from "./utils/sidebarContext"
import { LinkedToOperatorClient } from "../../models/Dialogs/linksToOperator"
import { getOperatorFeedbackStatus } from "../../components/Dialogs/DialogOperatorFeedbackForm/utility/feedbackStorage"
import { UploadedFile } from "../../utility/common/files"
import { HOT_KEY_MAP } from "../../components/HotKeysExtensionForm/HotKeysExtensionForm.const"

export interface DialogTopicsTreeData {
    rootId: string
    items: Record<string, DialogTopicsTreeItem>
}
export interface DialogTopicsTreeItemData {
    isSelected: boolean
    parentId: string
    title: string
}
export interface DialogTopicsTreeItem {
    id: string
    children: string[]
    hasChildren?: boolean
    isExpanded?: boolean
    isChildrenLoading?: boolean
    data: DialogTopicsTreeItemData
}

export interface DialogEmailData {
    subject: string
    attachments: MessageAttachment[]
    emailAnswerMode: EmailAnswerMode
}

export enum EmailAnswerMode {
    None = "",
    Reply = "Reply",
    Forward = "Forward"
}

export interface IAIAssistHint {
    alarm: boolean
    message: string
    supportArticles: Array<Record<string, string>>
}

export interface DialogOutgoingData {
    channel: SimpleChannel
    channelUserId: string
}

export interface ExportAttachmentsProgress {
    text: string
    progress: number
    isError: boolean
}

export enum DialogSidebarType {
    KnowledgeBase = "knowledgeBase",
    Article = "article",
    FastReplies = "fastReplies",
    Topics = "topics",
    PreviousDialog = "previousDialog",
    DialogHistory = "dialogHistory",
    ChannelSelect = "channelSelect",
    ClientCreate = "clientCreate",
    AddChannelToClient = "addChannelToClient",
    DialogInfo = "dialogInfo"
}

export type DialogsSlice = Readonly<{
    aiAssistHint: AsyncState<IAIAssistHint>
    aiSuggest: AsyncState<IAISuggestItemResponse[]>
    aiAssistRequestTypes: AsyncState<string[]>
    survey: AsyncState<SurveyForm>
    selectedDialogId?: string
    messagesPaginationState?: GetUpdatedMessagesRequest
    searchCriterion?: string
    searchOperatorByCriterionResponse: ISearchOperatorsByCriterionResponse[]
    searchOperatorByCriterionPrevArgs: ISearchOperatorsByCriterionRequest
    topics: {
        topicsSidebarDialogId: string
        treeData: DialogTopicsTreeData
    }
    inputLastCursorPosition: number
    shouldHandleCursorPosition: boolean
    knowledgeBase: {
        isKnowledgeBaseSidebarOpened: boolean
        isArticleSidebarOpened: boolean
    }
    fastReplies: {
        isFastRepliesSidebarOpened: boolean
        catalogHotKeys: FastRepliesHotKeyWithId[]
        hotKeys: HotKeyArticle[]
        showTooltip: boolean
    }
    currentStartMessage?: DialogStartMessage
    previousDialogs: {
        isPreviousDialogSidebarOpened: boolean
        previousDialogData: DialogStartMessage | null
    }
    dialogCreate: {
        creatingDialog: boolean
        isChannelSelectSidebarOpened: boolean
        isCreatingClientSidebarOpened: boolean
        dialogCreateOutgoingData: DialogOutgoingData | null
    }
    isAddChannelToClientSidebarOpened: boolean
    newUserChannelState: {
        isAdded: boolean
        channelId: string
    }
    searchData: {
        badges: DialogBadge[]
        clients: ISearchClientsByCriterionResponse[]
        linkedClients: LinkedToOperatorClient[]
    }
    currentOperatorMessage: string
    lastMessageInDialog?: ApiMessagesDU
    inputMessageViewData?: MessageInputViewData
    readonlyChat: ReadonlyChat
    isInputMaximized: boolean
    unsavedSurveys: string[]
    RMECommands: ContentEditorCommands | null
    dialogEmailData: DialogEmailData
    currentClientOmniUserId?: string
    videoCallState: VideoCallState
    dialogEmailMessageMeta: MessageMeta
    dialogEmailMessageSlots: MessageSlots
    operatorEmailChannel?: SimpleOperatorEmailChannel
    byLink: {
        channelId: string
        omniUserId: string
    }
    clientSubscriptionsProcess: boolean
    exportAttachmentsProgress: ExportAttachmentsProgress
    currentChannel?: SimpleChannelWithUserId
    dialogHistory: {
        isDialogHistorySidebarOpened: boolean
    }
    dialogsAutoHoldingTimers: Record<string, AutoHoldingTimer>
    userIsTypingMessage: UserIsTypingMessage
    isScreenSharingConnectionEstablished: boolean
    operatorFeedbackForm: {
        isOpened: boolean
        shouldShowButton: boolean
    }
    sidebarsContext: DialogSidebarType[]
    messageIdToScroll: string | null
    dialogBodyScrollable: boolean
    abortUploadingFile: (id: string, updatedAttachments: UploadedFile[]) => void | undefined
}>

const initTopicsTree: DialogTopicsTreeData = {
    rootId: "root",
    items: {
        root: {
            id: "root",
            children: [],
            data: {
                parentId: "",
                title: "",
                isSelected: false
            }
        }
    }
}

const initialState: DialogsSlice = {
    aiAssistHint: AsyncState.create(),
    aiSuggest: AsyncState.create(),
    aiAssistRequestTypes: AsyncState.create(),
    survey: AsyncState.create(),
    searchOperatorByCriterionPrevArgs: {
        query: "",
        statuses: [],
        queues: [],
        roles: [],
        includeCurrentUser: false
    },
    searchOperatorByCriterionResponse: [],
    topics: {
        topicsSidebarDialogId: "",
        treeData: initTopicsTree
    },
    inputLastCursorPosition: 0,
    shouldHandleCursorPosition: false,
    knowledgeBase: {
        isKnowledgeBaseSidebarOpened: false,
        isArticleSidebarOpened: false
    },
    fastReplies: {
        isFastRepliesSidebarOpened: false,
        catalogHotKeys: [],
        hotKeys: [],
        showTooltip: true
    },
    previousDialogs: {
        isPreviousDialogSidebarOpened: false,
        previousDialogData: null
    },
    dialogCreate: {
        creatingDialog: false,
        isChannelSelectSidebarOpened: false,
        isCreatingClientSidebarOpened: false,
        dialogCreateOutgoingData: null
    },
    isAddChannelToClientSidebarOpened: false,
    newUserChannelState: { isAdded: false, channelId: "" },
    searchData: {
        badges: [],
        clients: [],
        linkedClients: []
    },
    currentOperatorMessage: "",
    lastMessageInDialog: undefined,
    inputMessageViewData: undefined,
    readonlyChat: null,
    isInputMaximized: false,
    unsavedSurveys: [],
    RMECommands: null,
    dialogEmailData: {
        subject: "",
        attachments: [],
        emailAnswerMode: EmailAnswerMode.None
    },
    videoCallState: {
        isCollapsed: true,
        sessionStatus: SessionStatus.Loading
    },
    dialogEmailMessageMeta: {},
    dialogEmailMessageSlots: {},
    byLink: {
        channelId: "",
        omniUserId: ""
    },
    dialogHistory: {
        isDialogHistorySidebarOpened: false
    },
    clientSubscriptionsProcess: false,
    exportAttachmentsProgress: {
        text: "",
        progress: 0,
        isError: false
    },
    dialogsAutoHoldingTimers: {},
    userIsTypingMessage: {
        isTyping: false,
        message: ""
    },
    isScreenSharingConnectionEstablished: false,
    sidebarsContext: [],
    operatorFeedbackForm: {
        isOpened: false,
        shouldShowButton: getOperatorFeedbackStatus()
    },
    messageIdToScroll: null,
    dialogBodyScrollable: true,
    abortUploadingFile: () => void 0
}

const dialogs = createSlice({
    name: "dialogs",
    initialState,
    reducers: {
        setIsScreenSharingConnectionEstablished(state, action: PayloadAction<boolean>) {
            state.isScreenSharingConnectionEstablished = action.payload
        },
        getAIAssistHintProcess(state) {
            state.aiAssistHint = state.aiAssistHint.toProcess()
        },
        getAIAssistHintSuccess(state, action: PayloadAction<IAIAssistHint>) {
            state.aiAssistHint = state.aiAssistHint.toSuccess(action.payload)
        },
        getAIAssistHintFailed(state, action: PayloadAction<SystemError>) {
            state.aiAssistHint = state.aiAssistHint.toFailed(action.payload)
        },
        getAIAssistHintReset(state) {
            state.aiAssistHint = AsyncState.create()
        },
        getAISuggestProcess(state) {
            state.aiSuggest = state.aiSuggest.toProcess()
        },
        getAISuggestSuccess(state, action: PayloadAction<IAISuggestItemResponse[]>) {
            state.aiSuggest = state.aiSuggest.toSuccess(action.payload)
        },
        getAISuggestFailed(state, action: PayloadAction<SystemError>) {
            state.aiSuggest = state.aiSuggest.toFailed(action.payload)
        },
        getAISuggestReset(state) {
            state.aiSuggest = AsyncState.create()
        },
        getAIAssistRequestTypesProcess(state) {
            state.aiAssistRequestTypes = state.aiAssistRequestTypes.toProcess()
        },
        getAIAssistRequestTypesSuccess(state, action: PayloadAction<string[]>) {
            state.aiAssistRequestTypes = state.aiAssistRequestTypes.toSuccess(action.payload)
        },
        getAIAssistRequestTypesFailed(state, action: PayloadAction<SystemError>) {
            state.aiAssistRequestTypes = state.aiAssistRequestTypes.toFailed(action.payload)
        },
        setCursorPosition(state, action: PayloadAction<{ position: number; shouldHandleCursorPosition: boolean }>) {
            state.inputLastCursorPosition = action.payload.position
            state.shouldHandleCursorPosition = action.payload.shouldHandleCursorPosition
        },
        resetShouldHandleCursorPosition(state) {
            state.shouldHandleCursorPosition = false
        },
        setCurrentDialogId(state, action: PayloadAction<string>) {
            state.dialogCreate.creatingDialog = false
            state.dialogCreate.isCreatingClientSidebarOpened = false
            state.dialogCreate.isChannelSelectSidebarOpened = false

            updateSidebarContext(state, DialogSidebarType.ClientCreate, false)
            updateSidebarContext(state, DialogSidebarType.ChannelSelect, false)

            state.dialogCreate.dialogCreateOutgoingData = initialState.dialogCreate.dialogCreateOutgoingData
            state.selectedDialogId = action.payload
        },
        unsetCurrentDialogId(state) {
            state.selectedDialogId = undefined
        },
        setSearchCriterion(state, action: PayloadAction<string>) {
            state.searchCriterion = action.payload
        },
        getClientSurveyProcess(state) {
            state.survey = state.survey.toProcess()
        },
        getClientSurveySuccess(state, action: PayloadAction<SurveyForm>) {
            state.survey = state.survey.toSuccess(action.payload)
        },
        getClientSurveyFailed(state, action: PayloadAction<SystemError>) {
            state.survey = state.survey.toFailed(action.payload)
        },
        setMessagesPaginationState(state, action: PayloadAction<Partial<GetUpdatedMessagesRequest>>) {
            const newState = {
                ...state.messagesPaginationState,
                ...action.payload
            } as GetUpdatedMessagesRequest
            state.messagesPaginationState = newState
        },
        setSearchOperatorByCriterionRelatedData(
            state,
            action: PayloadAction<{
                args: ISearchOperatorsByCriterionRequest
                response: ISearchOperatorsByCriterionResponse[]
            }>
        ) {
            state.searchOperatorByCriterionPrevArgs = {
                ...state.searchOperatorByCriterionPrevArgs,
                ...action.payload.args
            }
            state.searchOperatorByCriterionResponse = action.payload.response
        },
        resetSearchOperatorByCriterionRelatedData(state) {
            state.searchOperatorByCriterionPrevArgs = { ...initialState.searchOperatorByCriterionPrevArgs }
            state.searchOperatorByCriterionResponse = []
        },
        openTopicsSidebar(state, action: PayloadAction<string>) {
            state.topics.topicsSidebarDialogId = action.payload
            updateSidebarContext(state, DialogSidebarType.Topics, true)
        },
        closeTopicsSidebar(state) {
            state.topics.topicsSidebarDialogId = ""
            const { treeData } = state.topics
            const { items, rootId } = treeData
            const treeDataIds = Object.keys(items).filter(itemId => itemId !== rootId)

            treeDataIds.forEach(topicId => (items[topicId].data.isSelected = false))

            updateSidebarContext(state, DialogSidebarType.Topics, false)
        },
        setTopicsData(state, action: PayloadAction<DialogTopic[]>) {
            state.topics.treeData = convertDialogTopicsToTree(action.payload)
        },
        openKnowledgeBaseSidebar(state) {
            state.knowledgeBase.isKnowledgeBaseSidebarOpened = true
            updateSidebarContext(state, DialogSidebarType.KnowledgeBase, true)
        },
        closeKnowledgeBaseSidebar(state) {
            state.knowledgeBase.isKnowledgeBaseSidebarOpened = false
            updateSidebarContext(state, DialogSidebarType.KnowledgeBase, false)
        },
        openFastRepliesSidebar(state) {
            state.fastReplies.isFastRepliesSidebarOpened = true
            updateSidebarContext(state, DialogSidebarType.FastReplies, true)
        },
        closeFastRepliesSidebar(state) {
            state.fastReplies.isFastRepliesSidebarOpened = false
            updateSidebarContext(state, DialogSidebarType.FastReplies, false)
        },
        openArticleSidebar(state) {
            state.knowledgeBase.isArticleSidebarOpened = true
            updateSidebarContext(state, DialogSidebarType.Article, true)
        },
        closeArticleSidebar(state) {
            state.knowledgeBase.isArticleSidebarOpened = false
            updateSidebarContext(state, DialogSidebarType.Article, false)
        },
        openPreviousDialogSidebar(state, action: PayloadAction<DialogStartMessage>) {
            state.previousDialogs.isPreviousDialogSidebarOpened = true
            state.previousDialogs.previousDialogData = action.payload
            updateSidebarContext(state, DialogSidebarType.PreviousDialog, true)
        },
        closePreviousDialogSidebar(state) {
            state.previousDialogs.isPreviousDialogSidebarOpened = false
            state.previousDialogs.previousDialogData = null
            updateSidebarContext(state, DialogSidebarType.PreviousDialog, false)
        },
        selectAndExpandDialogTopic(state, action: PayloadAction<DialogTopicsTreeItem>) {
            const { treeData } = state.topics
            const { items, rootId } = treeData

            const treeDataIds = Object.keys(items).filter(itemId => itemId !== rootId)
            const selectedIds = getListOfSelectedIds(treeData, action.payload)

            treeDataIds.forEach(topicId => {
                const topic = items[topicId]
                if (selectedIds.includes(topicId)) {
                    topic.data.isSelected = true
                    topic.isExpanded = topicId === action.payload.id ? !topic.isExpanded : true
                } else {
                    topic.data.isSelected = false
                }
            })
        },
        createDialog(state) {
            state.dialogCreate.creatingDialog = true
            state.dialogCreate.isChannelSelectSidebarOpened = true
            updateSidebarContext(state, DialogSidebarType.ChannelSelect, true)
        },
        finishCreatingDialog(state) {
            state.dialogCreate.creatingDialog = false
            state.dialogCreate.isCreatingClientSidebarOpened = false
            state.dialogCreate.isChannelSelectSidebarOpened = false
            state.dialogCreate.dialogCreateOutgoingData = initialState.dialogCreate.dialogCreateOutgoingData

            updateSidebarContext(state, DialogSidebarType.ClientCreate, false)
            updateSidebarContext(state, DialogSidebarType.ChannelSelect, false)
        },
        openCreateClientSidebar(state) {
            state.dialogCreate.isCreatingClientSidebarOpened = true

            updateSidebarContext(state, DialogSidebarType.ClientCreate, true)
        },
        closeCreateClientSidebar(state) {
            state.dialogCreate.isCreatingClientSidebarOpened = false

            updateSidebarContext(state, DialogSidebarType.ClientCreate, false)
        },
        openChannelSelectSidebar(state) {
            state.dialogCreate.isChannelSelectSidebarOpened = true

            updateSidebarContext(state, DialogSidebarType.ChannelSelect, true)
        },
        closeChannelSelectSidebar(state) {
            state.dialogCreate.isChannelSelectSidebarOpened = false

            updateSidebarContext(state, DialogSidebarType.ChannelSelect, false)
        },
        setDialogOutgoingData(state, action: PayloadAction<DialogOutgoingData | null>) {
            state.dialogCreate.dialogCreateOutgoingData = action.payload
        },
        setLastMessageInDialog(state, action: PayloadAction<ApiMessagesDU>) {
            state.lastMessageInDialog = action.payload
        },
        setInputMessageViewData(state, action: PayloadAction<MessageInputViewData | undefined>) {
            state.inputMessageViewData = action.payload
        },
        setInputMaximized(state, action: PayloadAction<boolean>) {
            state.isInputMaximized = action.payload
        },
        setRMECommands(state, action: PayloadAction<ContentEditorCommands>) {
            state.RMECommands = action.payload
        },
        setDialogEmailData(state, action: PayloadAction<DialogEmailData>) {
            state.dialogEmailData = action.payload
        },
        clearDialogEmailData(state) {
            state.dialogEmailData = initialState.dialogEmailData
        },
        setReadonlyChat(state, action: PayloadAction<ReadonlyChat>) {
            state.readonlyChat = action.payload
        },
        setCurrentClientOmniUserId(state, action: PayloadAction<string | undefined>) {
            state.dialogCreate.creatingDialog = false
            state.dialogCreate.isCreatingClientSidebarOpened = false
            state.dialogCreate.isChannelSelectSidebarOpened = false
            state.dialogCreate.dialogCreateOutgoingData = initialState.dialogCreate.dialogCreateOutgoingData
            state.currentClientOmniUserId = action.payload

            updateSidebarContext(state, DialogSidebarType.ClientCreate, false)
            updateSidebarContext(state, DialogSidebarType.ChannelSelect, false)
        },
        setOmniUserIdByLink(state, action: PayloadAction<string>) {
            state.byLink.omniUserId = action.payload
        },
        setChannelIdByLink(state, action: PayloadAction<string>) {
            state.byLink.channelId = action.payload
        },
        setSearchData(state, action: PayloadAction<Partial<DialogsSlice["searchData"]>>) {
            Object.assign(state.searchData, action.payload)
        },
        setVideoCallState(state, action: PayloadAction<Partial<VideoCallState>>) {
            Object.assign(state.videoCallState, action.payload)
        },
        toggleCollapsedVideoCallState(state) {
            state.videoCallState.isCollapsed = !state.videoCallState.isCollapsed
        },
        setDialogMessageEmailMeta(state, action: PayloadAction<MessageMeta>) {
            state.dialogEmailMessageMeta = action.payload
        },
        setDialogMessageEmailSlots(state, action: PayloadAction<MessageMeta>) {
            state.dialogEmailMessageSlots = action.payload
        },
        setCurrentStartMessage(state, action: PayloadAction<DialogStartMessage | undefined>) {
            state.currentStartMessage = action.payload
        },
        toggleUnsavedSurvey(state, { payload: [surveyId, isUnsaved] }: PayloadAction<[string, boolean]>) {
            state.unsavedSurveys = state.unsavedSurveys.filter(id => id !== surveyId)
            if (isUnsaved) {
                state.unsavedSurveys.push(surveyId)
            }
        },
        openDialogHistorySidebar(state) {
            state.dialogHistory.isDialogHistorySidebarOpened = true

            updateSidebarContext(state, DialogSidebarType.DialogHistory, true)
        },
        closeDialogHistorySidebar(state) {
            state.dialogHistory.isDialogHistorySidebarOpened = false

            updateSidebarContext(state, DialogSidebarType.DialogHistory, false)
        },
        setClientSubscriptionsProcess(state, action: PayloadAction<boolean>) {
            state.clientSubscriptionsProcess = action.payload
        },
        setOperatorEmailChannel(state, action: PayloadAction<SimpleOperatorEmailChannel | undefined>) {
            state.operatorEmailChannel = action.payload
        },
        setExportAttachmentsProgress(state, action: PayloadAction<Partial<ExportAttachmentsProgress>>) {
            state.exportAttachmentsProgress = { ...state.exportAttachmentsProgress, ...action.payload }
        },
        setCurrentChannel(state, action: PayloadAction<SimpleChannelWithUserId | undefined>) {
            state.currentChannel = action.payload
        },
        setAutoHoldingTimerForDialog(state, action: PayloadAction<Required<AutoHoldingMessage>>) {
            const { DialogId, AutoHolding } = action.payload

            state.dialogsAutoHoldingTimers[DialogId] = AutoHolding
        },
        removeAutoHoldingTimerFromDialog(state, action: PayloadAction<string>) {
            const dialogId = action.payload

            delete state.dialogsAutoHoldingTimers[dialogId]
        },
        openAddChannelToClientSidebar(state) {
            state.isAddChannelToClientSidebarOpened = true

            updateSidebarContext(state, DialogSidebarType.AddChannelToClient, true)
        },
        closeAddChannelToClientSidebar(state) {
            state.isAddChannelToClientSidebarOpened = false

            updateSidebarContext(state, DialogSidebarType.AddChannelToClient, false)
        },
        setNewUserChannelState(state, action: PayloadAction<{ isAdded: boolean; channelId: string }>) {
            const { isAdded, channelId } = action.payload

            state.newUserChannelState = {
                isAdded,
                channelId
            }
        },
        setIsUserTyping(state, action: PayloadAction<UserIsTypingMessage>) {
            state.userIsTypingMessage = action.payload
        },
        setFastRepliesHotKeys(
            state,
            action: PayloadAction<{ hotKeys: FastRepliesHotKeyWithId[]; articles: CategoryTreeItem[]; rootId?: string }>
        ) {
            const { hotKeys, articles, rootId } = action.payload

            const filteredHotKeys = rootId ? hotKeys.filter(hotKey => hotKey.CatalogSymbol === rootId) : hotKeys

            state.fastReplies.catalogHotKeys = filteredHotKeys

            state.fastReplies.hotKeys = []

            filteredHotKeys.forEach(hotKey => {
                const article = articles.find(article => article.data.symbolCode === hotKey.ArticleSymbol)

                const firstKey = hotKey.FirstKey
                const secondKey = HOT_KEY_MAP.find(key => key.includes(hotKey.SecondKey)) || []

                state.fastReplies.hotKeys.push({
                    firstKey,
                    secondKey,
                    text: article?.data.answer || article?.data.title || ""
                })
            })
        },
        setFastRepliesTooltipState(state, action: PayloadAction<boolean>) {
            state.fastReplies.showTooltip = action.payload
        },
        // Стейт этоого сайдбара сейчас частично в хуке useDialogsSidebar
        // TODO: Переместить в редакс при следующей итерации рефакторинга сайдбаров
        openDialogInfoSidebar(state) {
            updateSidebarContext(state, DialogSidebarType.DialogInfo, true)
        },
        closeDialogInfoSidebar(state) {
            updateSidebarContext(state, DialogSidebarType.DialogInfo, false)
        },
        setMessageIdToScroll(state, action: PayloadAction<string | null>) {
            state.messageIdToScroll = action.payload
        },
        setOperatorFeedbackFormState(state, action: PayloadAction<boolean>) {
            state.operatorFeedbackForm.isOpened = action.payload
        },
        setOperatorFeedbackFormButtonState(state, action: PayloadAction<boolean>) {
            state.operatorFeedbackForm.shouldShowButton = action.payload
        },
        setDialogBodyScrollable(state, action: PayloadAction<boolean>) {
            state.dialogBodyScrollable = action.payload
        },
        setAbortUploadingFile: (
            state,
            action: PayloadAction<(id: string, updatedAttachments: UploadedFile[]) => void | undefined>
        ) => {
            state.abortUploadingFile = action.payload
        },
        setCurrentOperatorMessage: (state, action: PayloadAction<string | undefined>) => {
            state.currentOperatorMessage = action.payload || ""
        }
    },
    extraReducers: builder => {
        builder.addCase(resetReducerState, () => {
            return initialState
        })
    }
})

export const DialogsReducer = dialogs.reducer
export const { actions } = dialogs
